import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { Bold, Italic, Heading1, Heading2, Type as TypeIcon, List, ListOrdered } from 'lucide-react';
import { useEffect } from 'react';

const MenuBar = ({ editor }) => {
  if (!editor) return null;

  const btnClass = (active) =>
    `p-1.5 rounded transition-colors ${
      active
        ? 'bg-[#00D9C8]/20 text-[#00D9C8]'
        : 'text-[#A0AEC0] hover:bg-[#1A2B42] hover:text-white'
    }`;

  const textBtnClass = (active) =>
    `px-2 py-1 rounded transition-colors text-xs flex items-center gap-1 ${
      active
        ? 'bg-[#00D9C8]/20 text-[#00D9C8]'
        : 'text-[#A0AEC0] hover:bg-[#1A2B42] hover:text-white'
    }`;

  return (
    <div className="flex items-center gap-0.5 bg-[#0A1628] rounded-lg px-2 py-1.5" data-testid="blog-formatting-toolbar">
      <button
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={btnClass(editor.isActive('bold'))}
        title="Fett"
        data-testid="blog-bold-btn"
      >
        <Bold className="w-4 h-4" />
      </button>
      <button
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={btnClass(editor.isActive('italic'))}
        title="Kursiv"
        data-testid="blog-italic-btn"
      >
        <Italic className="w-4 h-4" />
      </button>

      <div className="w-px h-5 bg-[#1A2B42] mx-1" />

      <button
        onClick={() => editor.chain().focus().setParagraph().run()}
        className={textBtnClass(editor.isActive('paragraph') && !editor.isActive('heading'))}
        title="Normaler Text"
        data-testid="blog-text-btn"
      >
        <TypeIcon className="w-3.5 h-3.5" /> Text
      </button>
      <button
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        className={textBtnClass(editor.isActive('heading', { level: 2 }))}
        title="Überschrift"
        data-testid="blog-heading-btn"
      >
        <Heading1 className="w-3.5 h-3.5" /> Überschrift
      </button>
      <button
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        className={textBtnClass(editor.isActive('heading', { level: 3 }))}
        title="Unterüberschrift"
        data-testid="blog-subheading-btn"
      >
        <Heading2 className="w-3.5 h-3.5" /> Unterüberschrift
      </button>

      <div className="w-px h-5 bg-[#1A2B42] mx-1" />

      <button
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={btnClass(editor.isActive('bulletList'))}
        title="Aufzählung"
      >
        <List className="w-4 h-4" />
      </button>
      <button
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={btnClass(editor.isActive('orderedList'))}
        title="Nummerierung"
      >
        <ListOrdered className="w-4 h-4" />
      </button>
    </div>
  );
};

// Convert HTML to plain text for character counting
const htmlToPlainText = (html) => {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

// Convert markdown-like text_content to HTML for initial load
const textToHtml = (text) => {
  if (!text) return '';
  // If it already looks like HTML, return as-is
  if (text.startsWith('<')) return text;
  // Convert markdown to HTML
  return text
    .split('\n\n')
    .map(block => {
      block = block.trim();
      if (!block) return '';
      if (block.startsWith('### ')) return `<h3>${block.slice(4)}</h3>`;
      if (block.startsWith('## ')) return `<h2>${block.slice(3)}</h2>`;
      if (block.startsWith('# ')) return `<h1>${block.slice(2)}</h1>`;
      // Bold and italic inline
      block = block.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
      block = block.replace(/\*(.+?)\*/g, '<em>$1</em>');
      return `<p>${block}</p>`;
    })
    .join('');
};

export const BlogEditor = ({ content, onChange, maxChars, placeholder }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
      }),
      Placeholder.configure({
        placeholder: placeholder || 'Schreibe deinen Blogartikel...',
      }),
    ],
    content: textToHtml(content),
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'blog-editor-content',
      },
    },
  });

  // Sync external content changes (e.g. regeneration)
  useEffect(() => {
    if (editor && content !== undefined) {
      const currentHtml = editor.getHTML();
      const newHtml = textToHtml(content);
      // Only update if content fundamentally changed (not from our own edits)
      if (newHtml && currentHtml !== newHtml && !content.startsWith('<')) {
        editor.commands.setContent(newHtml);
      }
    }
  }, [content]);

  const plainText = editor ? htmlToPlainText(editor.getHTML()) : '';
  const charCount = plainText.length;
  const wordCount = plainText.trim() ? plainText.trim().split(/\s+/).length : 0;
  const isOverLimit = maxChars && charCount > maxChars;
  const isNearLimit = maxChars && charCount > maxChars * 0.9;

  return (
    <div className="space-y-0">
      <style>{`
        .blog-editor-content {
          min-height: 300px;
          outline: none;
          color: white;
          font-size: 15px;
          line-height: 1.7;
          padding: 12px;
        }
        .blog-editor-content p { margin-bottom: 0.75em; }
        .blog-editor-content h2 {
          font-size: 1.4em;
          font-weight: 700;
          color: #fff;
          margin: 1em 0 0.5em;
        }
        .blog-editor-content h3 {
          font-size: 1.15em;
          font-weight: 600;
          color: #cbd5e1;
          margin: 0.8em 0 0.4em;
        }
        .blog-editor-content strong { font-weight: 700; color: #fff; }
        .blog-editor-content em { font-style: italic; color: #cbd5e1; }
        .blog-editor-content ul, .blog-editor-content ol {
          padding-left: 1.5em;
          margin-bottom: 0.75em;
        }
        .blog-editor-content li { margin-bottom: 0.25em; }
        .blog-editor-content p.is-editor-empty:first-child::before {
          color: #5A6C7D;
          content: attr(data-placeholder);
          float: left;
          height: 0;
          pointer-events: none;
        }
        .ProseMirror:focus { outline: none; }
      `}</style>

      <MenuBar editor={editor} />

      <div className="bg-[#1A2B42] rounded-lg border border-transparent focus-within:border-[#00D9C8]/40 transition-colors">
        <EditorContent editor={editor} />
      </div>

      {maxChars && (
        <div className={`flex items-center justify-between text-xs px-2 py-1.5 rounded ${
          isOverLimit ? 'bg-red-500/20 text-red-400' :
          isNearLimit ? 'bg-yellow-500/20 text-yellow-400' :
          'bg-[#0A1628] text-[#5A6C7D]'
        }`}>
          <span>{wordCount.toLocaleString()} Wörter &middot; {charCount.toLocaleString()} / {maxChars.toLocaleString()} Zeichen</span>
          {isOverLimit && <span className="font-medium">{(charCount - maxChars).toLocaleString()} zu viel!</span>}
        </div>
      )}
    </div>
  );
};

// Helper to convert TipTap HTML back to plain text for storage
export const blogHtmlToText = (html) => htmlToPlainText(html);
